import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const AddPlaceDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: null,
    longitude: null,
    default_notes: '',
    default_reminder_meter: 15,
    default_reminder_leave: 60,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      latitude: null,
      longitude: null,
      default_notes: '',
      default_reminder_meter: 15,
      default_reminder_leave: 60,
    });
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }

    setLocationLoading(true);
    
    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            {
              headers: {
                'Accept': 'application/json',
              }
            }
          );
          
          if (!response.ok) {
            throw new Error('Geocoding failed');
          }
          
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            address: data.display_name || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            latitude,
            longitude,
          }));
          toast.success('Location captured!');
        } catch (error) {
          console.error('Geocoding error:', error);
          setFormData(prev => ({
            ...prev,
            address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            latitude,
            longitude,
          }));
          toast.success('Location coordinates captured!');
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        setLocationLoading(false);
        let errorMessage = 'Could not get your location.';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please try again.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'Could not get your location. Please enter manually.';
        }
        
        toast.error(errorMessage);
      },
      options
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter a name for this place');
      return;
    }
    
    setLoading(true);
    try {
      const dataToSend = {
        name: formData.name,
        address: formData.address || null,
        latitude: formData.latitude || null,
        longitude: formData.longitude || null,
        default_notes: formData.default_notes || null,
        default_reminder_meter: parseInt(formData.default_reminder_meter) || 15,
        default_reminder_leave: parseInt(formData.default_reminder_leave) || 60,
      };
      
      await API.post('/parking/places', dataToSend);
      toast.success('Place saved!');
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Failed to save place:', error);
      toast.error('Failed to save place');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Save a Parking Place</DialogTitle>
          <DialogDescription>
            Save places you park often for quick access
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Place Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Work, Mall, Gym"
              data-testid="input-place-name"
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="address">Address</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleGetLocation}
                disabled={locationLoading}
                data-testid="place-use-current-location-btn"
              >
                {locationLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Getting...
                  </>
                ) : (
                  <>
                    <Navigation className="h-4 w-4 mr-1" />
                    Use Current
                  </>
                )}
              </Button>
            </div>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Street address"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="default_notes">Default Notes</Label>
            <Textarea
              id="default_notes"
              value={formData.default_notes}
              onChange={(e) => handleChange('default_notes', e.target.value)}
              placeholder="Notes to auto-fill when parking here"
              rows={2}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="default_reminder_meter">Meter Reminder (min)</Label>
              <Input
                id="default_reminder_meter"
                type="number"
                value={formData.default_reminder_meter}
                onChange={(e) => handleChange('default_reminder_meter', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_reminder_leave">Leave Reminder (min)</Label>
              <Input
                id="default_reminder_leave"
                type="number"
                value={formData.default_reminder_leave}
                onChange={(e) => handleChange('default_reminder_leave', e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#D4C5E2] hover:bg-[#C4B5D2] text-gray-900"
              data-testid="submit-place-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Place
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPlaceDialog;
